/**
 * Shared constants for PGM education programs
 * These constants ensure consistency across all university pages
 */

/**
 * Required internship months for PGM program completion
 */
export const REQUIRED_INTERNSHIP_MONTHS = 16;

/**
 * Education level codes
 * V-prefix = PGM 3.1 (Vocational)
 * S-prefix = PGM 3.0 (Standard)
 * U-prefix = PGM 2.0 (University)
 */
export const EDUCATION_LEVEL_CODES = {
  // PGM 3.1 codes
  V0: "V0",
  V1: "V1",
  V2: "V2",
  V3: "V3",
  // PGM 3.0 codes
  S0: "S0",
  S1: "S1",
  S2: "S2",
  S3: "S3",
  // PGM 2.0 codes
  U0: "U0",
  U1: "U1",
  U2: "U2",
  U3: "U3"
} as const;

export type EducationLevelCode = (typeof EDUCATION_LEVEL_CODES)[keyof typeof EDUCATION_LEVEL_CODES];

/**
 * Program versions
 */
export const PGM_VERSIONS = {
  v2_0: "2.0",
  v3_0: "3.0",
  v3_1: "3.1"
} as const;

/**
 * Program version display names
 */
export const PGM_VERSION_NAMES = {
  [PGM_VERSIONS.v3_0]: "PGM 3.0",
  [PGM_VERSIONS.v3_1]: "PGM 3.1",
  [PGM_VERSIONS.v2_0]: "PGM 2.0"
} as const;

/**
 * Level code to program version mapping
 */
export const LEVEL_CODE_TO_VERSION: Record<string, string> = {
  V: PGM_VERSIONS.v3_1,
  S: PGM_VERSIONS.v3_0,
  U: PGM_VERSIONS.v2_0
};

/**
 * Program version to level code prefix mapping
 */
export const VERSION_TO_LEVEL_PREFIX: Record<string, string> = {
  [PGM_VERSION_NAMES[PGM_VERSIONS.v3_0]]: "S",
  [PGM_VERSION_NAMES[PGM_VERSIONS.v3_1]]: "V",
  [PGM_VERSION_NAMES[PGM_VERSIONS.v2_0]]: "U"
};

/**
 * Level code to numeric level mapping
 */
export const LEVEL_CODE_TO_NUMBER: Record<EducationLevelCode, number> = {
  V0: 0,
  V1: 1,
  V2: 2,
  V3: 3,
  S0: 0,
  S1: 1,
  S2: 2,
  S3: 3,
  U0: 0,
  U1: 1,
  U2: 2,
  U3: 3
};

/**
 * Level code to display name mapping
 */
export const LEVEL_CODE_TO_DISPLAY: Record<EducationLevelCode, string> = {
  V0: "Level 0",
  V1: "Level 1",
  V2: "Level 2",
  V3: "Level 3",
  S0: "Level 0",
  S1: "Level 1",
  S2: "Level 2",
  S3: "Level 3",
  U0: "Level 0",
  U1: "Level 1",
  U2: "Level 2",
  U3: "Level 3"
};

/**
 * Program level options for roster editing (V/S codes)
 * Used in: eligible-for-enrollment program level change dialog
 */
export const ROSTER_PROGRAM_LEVEL_OPTIONS = [
  { value: EDUCATION_LEVEL_CODES.V0, label: "PGM 3.1 Level 0" },
  { value: EDUCATION_LEVEL_CODES.V1, label: "PGM 3.1 Level 1" },
  { value: EDUCATION_LEVEL_CODES.V2, label: "PGM 3.1 Level 2" },
  { value: EDUCATION_LEVEL_CODES.V3, label: "PGM 3.1 Level 3" },
  { value: EDUCATION_LEVEL_CODES.S0, label: "PGM 3.0 Level 0" },
  { value: EDUCATION_LEVEL_CODES.S1, label: "PGM 3.0 Level 1" },
  { value: EDUCATION_LEVEL_CODES.S2, label: "PGM 3.0 Level 2" },
  { value: EDUCATION_LEVEL_CODES.S3, label: "PGM 3.0 Level 3" },
  { value: EDUCATION_LEVEL_CODES.U0, label: "PGM 2.0 Level 0" },
  { value: EDUCATION_LEVEL_CODES.U1, label: "PGM 2.0 Level 1" },
  { value: EDUCATION_LEVEL_CODES.U2, label: "PGM 2.0 Level 2" },
  { value: EDUCATION_LEVEL_CODES.U3, label: "PGM 2.0 Level 3" }
];

/**
 * Program level filter options (version + level format)
 * Used in: filter dropdowns for current students, graduation eligible, etc.
 */
export const FILTER_PROGRAM_LEVEL_OPTIONS = [
  { value: "3.1 3", label: "PGM 3.1 Level 3" },
  { value: "3.1 2", label: "PGM 3.1 Level 2" },
  { value: "3.1 1", label: "PGM 3.1 Level 1" },
  { value: "3.1 0", label: "PGM 3.1 Level 0" },
  { value: "3.0 3", label: "PGM 3.0 Level 3" },
  { value: "3.0 2", label: "PGM 3.0 Level 2" },
  { value: "3.0 1", label: "PGM 3.0 Level 1" },
  { value: "3.0 0", label: "PGM 3.0 Level 0" },
  { value: "2.0 3", label: "PGM 2.0 Level 3" },
  { value: "2.0 2", label: "PGM 2.0 Level 2" },
  { value: "2.0 1", label: "PGM 2.0 Level 1" },
  { value: "2.0 0", label: "PGM 2.0 Level 0" }
];

/**
 * Level code filter options (level codes only format)
 * Format: { value: "V3", label: "V3" } - matches GraphQL levelCodes input
 */
export const LEVEL_CODE_FILTER_OPTIONS: Array<{ value: EducationLevelCode; label: string }> = Object.values(
  EDUCATION_LEVEL_CODES
).map((code) => ({
  value: code,
  label: code
}));

/**
 * Program version options
 * Used in: upload roster page
 */
export const PROGRAM_VERSION_OPTIONS = [PGM_VERSION_NAMES[PGM_VERSIONS.v3_0], PGM_VERSION_NAMES[PGM_VERSIONS.v3_1]];

/**
 * Program version filter options (version number format)
 * Used in: filter dropdowns for alumni table and other tables that filter by version only
 * Includes legacy version 2.0 for historical data support
 */
export const PROGRAM_VERSION_FILTER_OPTIONS = [
  { value: PGM_VERSIONS.v3_1, label: PGM_VERSIONS.v3_1 },
  { value: PGM_VERSIONS.v3_0, label: PGM_VERSIONS.v3_0 },
  { value: PGM_VERSIONS.v2_0, label: PGM_VERSIONS.v2_0 }
];

/**
 * PAT (Playing Ability Test) Status options
 * Used in: filter dropdowns across student tables
 */
export const PAT_STATUS_OPTIONS = [
  { value: "NOT_STARTED", label: "Not Started" },
  { value: "REGISTERED", label: "Registered" },
  { value: "PENDING", label: "Pending" },
  { value: "PASSED", label: "Passed" },
  { value: "QUALIFYING", label: "Qualifying" },
  { value: "FAILED", label: "Failed" }
];

/**
 * Mapping of frontend PAT status values to GraphQL enum values
 */
export const PAT_STATUS_TO_GRAPHQL_MAP: Record<string, string> = {
  FAILED: "FAILED",
  PASSED: "PASSED",
  QUALIFYING: "QUALIFIED",
  SUSPENDED: "SUSPENDED"
};

/**
 * Map frontend PAT status values to GraphQL enum values
 * @param statuses - Array of frontend PAT status values
 * @returns Array of GraphQL enum values, or undefined if empty
 */
export function mapPATStatusesToGraphQL(statuses: string[]): string[] | undefined {
  if (statuses.length === 0) return undefined;

  const mappedStatuses = statuses.map((status) => PAT_STATUS_TO_GRAPHQL_MAP[status] || status);

  return mappedStatuses.length > 0 ? mappedStatuses : undefined;
}

export const PAT_STATUS_TITLE_TO_UPPERCASE_MAP: Record<string, string> = {
  "Not Started": "NOT_STARTED",
  Registered: "REGISTERED",
  Pending: "PENDING",
  Failed: "FAILED",
  Passed: "PASSED",
  Qualifying: "QUALIFYING",
  "In Progress": "IN_PROGRESS",
  Suspended: "SUSPENDED"
};

export function convertPATStatusToUppercase(status: string): string {
  return PAT_STATUS_TITLE_TO_UPPERCASE_MAP[status] || status.toUpperCase().replace(/\s+/g, "_");
}

/**
 * Format a program level code (e.g., "V1", "S2") to a display string
 * @param levelCode - The raw level code (V0-V3, S0-S3)
 * @returns Formatted string like "PGM 3.1 Level 1"
 * @example
 * formatProgramLevel("V1") // "PGM 3.1 Level 1"
 * formatProgramLevel("S2") // "PGM 3.0 Level 2"
 */
export function formatProgramLevel(levelCode: string): string {
  // Determine program version from the code prefix
  const prefix = levelCode.charAt(0);
  const version = LEVEL_CODE_TO_VERSION[prefix] || PGM_VERSIONS.v3_1;

  // Map level codes to display levels
  const level = LEVEL_CODE_TO_DISPLAY[levelCode as EducationLevelCode] || levelCode;

  return `${PGM_VERSION_NAMES[version as keyof typeof PGM_VERSION_NAMES]} ${level}`;
}

/**
 * Convert a level code to its numeric value
 * @param levelCode - The raw level code (V0-V3, S0-S3)
 * @returns Numeric level (0-3)
 * @example
 * getLevelNumber("V2") // 2
 * getLevelNumber("S0") // 0
 */
export function getLevelNumber(levelCode: string): number {
  return LEVEL_CODE_TO_NUMBER[levelCode as EducationLevelCode] ?? 0;
}

/**
 * Generate a level code from version and level number
 * @param version - Program version ("3.0" or "3.1")
 * @param level - Level number (0-3)
 * @returns Level code like "V2" or "S1"
 * @example
 * generateLevelCode("3.1", 2) // "V2"
 * generateLevelCode("3.0", 1) // "S1"
 */
export function generateLevelCode(version: string, level: number): string {
  const versionName =
    version === PGM_VERSIONS.v3_0 ? PGM_VERSION_NAMES[PGM_VERSIONS.v3_0] : PGM_VERSION_NAMES[PGM_VERSIONS.v3_1];
  const prefix = VERSION_TO_LEVEL_PREFIX[versionName];
  return `${prefix}${level}`;
}

/**
 * Get program version from level code
 * @param levelCode - The raw level code (V0-V3, S0-S3)
 * @returns Program version ("3.0" or "3.1")
 * @example
 * getVersionFromLevelCode("V2") // "3.1"
 * getVersionFromLevelCode("S1") // "3.0"
 */
export function getVersionFromLevelCode(levelCode: string): string {
  const prefix = levelCode.charAt(0);
  return LEVEL_CODE_TO_VERSION[prefix] || PGM_VERSIONS.v3_1;
}
